import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";

// SEO Configuration for each page
const SEO_CONFIG = {
  home: {
    title: "Elisabeth Troestler | Artiste Peintre",
    description: "Elisabeth Troestler, artiste peintre française à Neuilly-sur-Seine. Huiles sur toile, de la figuration à l'abstraction lyrique. Découvrez ses collections, expositions et œuvres originales."
  },
  collections: {
    title: "Œuvres d'Elisabeth Troestler – Collections et séries de peintures",
    description: "Découvrez les collections d'Elisabeth Troestler : Line and Music, Série Rose, Série Rouge, Sur la terre comme au ciel et d'autres séries de peintures abstraites."
  },
  bio: {
    title: "Biographie d'Elisabeth Troestler – Artiste peintre française",
    description: "Biographie d'Elisabeth Troestler : études aux Beaux-Arts, parcours international, installation à Neuilly-sur-Seine et évolution vers l'abstraction lyrique."
  },
  exhibitions: {
    title: "Expositions d'Elisabeth Troestler – Timeline et événements",
    description: "Découvrez les expositions passées et à venir d'Elisabeth Troestler, artiste peintre française spécialisée dans l'abstraction lyrique."
  },
  contact: {
    title: "Contact – Atelier d'Elisabeth Troestler, artiste peintre",
    description: "Contacter Elisabeth Troestler pour une exposition, une acquisition ou une commande d'œuvre. Téléphone et email."
  },
  press: {
    title: "Presse – Elisabeth Troestler, artiste peintre",
    description: "Articles de presse et publications sur Elisabeth Troestler, artiste peintre française."
  }
};

// SEO Component
const PageSEO = ({ page }) => {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  return (
    <Helmet>
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta name="twitter:title" content={config.title} />
      <meta name="twitter:description" content={config.description} />
    </Helmet>
  );
};

// Landing page images from Dropbox
const LANDING_IMAGES = [
  {
    url: "https://www.dropbox.com/scl/fo/7hfpckqt50tdhp1w7jmrr/AEkGJ5hyBJjvAJOF_ffGZdc/LP_01.jpg?rlkey=hw96ol2xoz3zk26hpba1qti2k&raw=1",
    alt: "Elisabeth Troestler Artwork 1"
  },
  {
    url: "https://www.dropbox.com/scl/fo/7hfpckqt50tdhp1w7jmrr/ANwClZNwjTMMEYvgjQSoyeE/LP_02.png?rlkey=hw96ol2xoz3zk26hpba1qti2k&raw=1",
    alt: "Elisabeth Troestler Artwork 2"
  },
  {
    url: "https://www.dropbox.com/scl/fo/7hfpckqt50tdhp1w7jmrr/AHL4d03ST3jZVBGycWQJwko/LP_03.jpg?rlkey=hw96ol2xoz3zk26hpba1qti2k&raw=1",
    alt: "Elisabeth Troestler Artwork 3"
  },
  {
    url: "https://www.dropbox.com/scl/fo/7hfpckqt50tdhp1w7jmrr/APKcZ-xVjb0UmklqDVwup8g/LP_04.png?rlkey=hw96ol2xoz3zk26hpba1qti2k&raw=1",
    alt: "Elisabeth Troestler Artwork 4"
  },
  {
    url: "https://www.dropbox.com/scl/fo/7hfpckqt50tdhp1w7jmrr/AFYj2Av__MnV9EC-fuRUyos/LP_05.jpg?rlkey=hw96ol2xoz3zk26hpba1qti2k&raw=1",
    alt: "Elisabeth Troestler Artwork 5"
  }
];

// Collections data
// Collections organized by category
const THEMES_COLLECTIONS = [
  {
    id: "reves-bleus",
    name: "Rêves bleus",
    slug: "reves-bleus"
  },
  {
    id: "line-and-music",
    name: "Line and Music",
    slug: "line-and-music"
  },
  {
    id: "sur-la-terre-comme-au-ciel",
    name: "Sur la terre comme au ciel",
    slug: "sur-la-terre-comme-au-ciel"
  },
  {
    id: "permanence-et-mouvement",
    name: "Permanence et mouvement",
    slug: "permanence-et-mouvement"
  },
  {
    id: "blue-blues",
    name: "Blue Blues",
    slug: "blue-blues"
  },
  {
    id: "actualite-et-abstractions",
    name: "Actualité et abstractions",
    slug: "actualite-et-abstractions"
  },
  {
    id: "closoirs",
    name: "Écho du Moyen Âge",
    slug: "closoirs",
    subtitle: "2020-2022 Huile sur bois 24x33cm"
  },
  {
    id: "aventures-de-yellow",
    name: "Aventures de Yellow",
    slug: "aventures-de-yellow",
    subtitle: "2002-2007, Collages sur papier, format A6 par 4"
  }
];

const SERIES_COLLECTIONS = [
  {
    id: "serie-rose",
    name: "Série rose et mauve",
    slug: "serie-rose"
  },
  {
    id: "serie-bleues",
    name: "Série bleue",
    slug: "serie-bleues"
  },
  {
    id: "serie-rouges",
    name: "Série rouge",
    slug: "serie-rouges"
  },
  {
    id: "serie-vertes",
    name: "Série verte",
    slug: "serie-vertes"
  }
];

// Combined collections for lookups
const COLLECTIONS = [...THEMES_COLLECTIONS, ...SERIES_COLLECTIONS];

// Collection thumbnails data for overview page (sorted by Order from Google Sheets)
// URLs use Dropbox share links with ?raw=1 format for permanent access
const COLLECTION_THUMBNAILS = [
  {
    id: "colnav-12",
    slug: "reves-bleus",
    name: "Rêves bleus",
    year: "2025",
    technic: "Huile sur toile",
    order: 0,
    url: "/images/reves_bleus/thumbnail2.jpg"
  },
  {
    id: "colnav-02",
    slug: "blue-blues",
    name: "Blue Blues",
    year: "2024",
    technic: "Huile sur toile",
    order: 1,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/ABZf1k_sKlMMIdNfEPmyUh0/Colnav_02.png?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-04",
    slug: "permanence-et-mouvement",
    name: "Permanence et mouvement",
    year: "2010",
    technic: "Huile sur toile",
    order: 2,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/ANrlKO5AJ6qkdc_bR4uI6_E/Colnav_04.jpg?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-03",
    slug: "sur-la-terre-comme-au-ciel",
    name: "Sur la terre comme au ciel",
    year: "2008",
    technic: "Huile sur toile",
    order: 3,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/ADZTmJdXQGBwbgIjdG_vbYA/Colnav_03.png?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-01",
    slug: "line-and-music",
    name: "Line and Music",
    year: "2003-2004",
    technic: "Huile sur toile",
    order: 4,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AGw2pbU6s7W0VYtNVR6D8J8/Colnav_01.png?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-08",
    slug: "serie-rose",
    name: "Série rose et mauve",
    year: "",
    technic: "Huile sur toile",
    order: 5,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AIlpKIsq0sO3k4-BoKWWFZg/Colnav_08.jpg?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-09",
    slug: "serie-bleues",
    name: "Série bleue",
    year: "",
    technic: "Huile sur toile",
    order: 6,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AIkWLa2CBdtNaNwn8UthdFA/Colnav_09.png?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-10",
    slug: "serie-rouges",
    name: "Série rouge",
    year: "",
    technic: "Huile sur toile",
    order: 7,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AJDZehMxL4pqUbxagJ6HEyk/Colnav_10.jpg?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-11",
    slug: "serie-vertes",
    name: "Série verte",
    year: "",
    technic: "Huile sur toile",
    order: 8,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AFcixkv0iVh3adx74EFQoiw/Colnav_11.png?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-05",
    slug: "actualite-et-abstractions",
    name: "Actualité et abstractions",
    year: "",
    technic: "Technique mixte",
    order: 9,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AEGs12FnsV1ouYXBTUQrON4/Colnav_05.jpg?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-06",
    slug: "closoirs",
    name: "Écho du Moyen Âge",
    year: "2022",
    technic: "Huile sur bois",
    order: 10,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/AMCxN7T0YqvIKGwhoFlCYA8/Colnav_06.JPG?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  },
  {
    id: "colnav-07",
    slug: "aventures-de-yellow",
    name: "Aventures de Yellow",
    year: "2002-2007",
    technic: "Collages",
    order: 11,
    url: "https://www.dropbox.com/scl/fo/0xborg6ppww6lv51w81u1/ANFifhYuPbogczepMojAif4/Colnav_07.JPG?rlkey=hg7zzreoc8ybixwekhe1tmdwu&raw=1"
  }
].sort((a, b) => a.order - b.order);

// Line and Music artworks from Dropbox with metadata from Google Sheets
const LINE_AND_MUSIC_ARTWORKS = [
  {
    id: "lm-01",
    fileName: "LM_01.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AFbUo6ePDwOqckkai-n0DpE/LM_01.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Morning Music",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "lm-02",
    fileName: "LM_02.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AGga_PPT59652I1EN7UwVj0/LM_02.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Evening Blues",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "lm-03",
    fileName: "LM_03.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AF7d955AD3sNFHoqiQeteWE/LM_03.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Grey Composition (\"Goldberg Variation\" J.S. Bach)",
    year: "2003",
    size: "116x90cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "lm-04",
    fileName: "LM_04.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/ALiqrrZ_l1wPAENG6PZ-l5o/LM_04.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Green Composition (\"The 6 Cello Suites\" J.S. Bach)",
    year: "2003",
    size: "116x90cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "lm-05",
    fileName: "LM_05.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/ADWCeD0m-m6wEEmYTQrL5Zs/LM_05.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "My favorite things (John Coltrane)",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "lm-06",
    fileName: "LM_06.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/APMEnR5tYuFy9AvMpwybxqY/LM_06.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Trane's Slow Blues (John Coltrane)",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 6
  },
  {
    id: "lm-07",
    fileName: "LM_07.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AJuexDAUxJIRibRj6nJsyrQ/LM_07.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Mineral Composition (Homage to Cesaria Evora)",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 7
  },
  {
    id: "lm-08",
    fileName: "LM_08.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/ANiFKzuRSeHQpodSIVFJJmg/LM_08.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Aborigene's vibration",
    year: "2004",
    size: "120x100cm",
    technic: "Oil on canvas",
    order: 8
  },
  {
    id: "lm-09",
    fileName: "LM_09.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/ALCALDc7wuoAN3dKo57RGeg/LM_09.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Oblivion (Homage to Astor Piazolla)",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 9
  },
  {
    id: "lm-10",
    fileName: "LM_10.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AHtaJVIFRUnMsSH6kU3zHnc/LM_10.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "TAM! TAM!",
    year: "2004",
    size: "120x100cm",
    technic: "Oil on canvas",
    order: 10
  },
  {
    id: "lm-11",
    fileName: "LM_11.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AByXHPPoZyJnHfMqT3JX5IA/LM_11.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Pacific's sunrise",
    year: "2004",
    size: "120x100cm",
    technic: "Oil on canvas",
    order: 11
  },
  {
    id: "lm-12",
    fileName: "LM_12.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/ALAZ-gPOTT2vGKRyX09V5Ao/LM_12.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "String's vibrations",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 12
  },
  {
    id: "lm-13",
    fileName: "LM_13.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AEDJeKs1azeHLvPgUdXXOq4/LM_13.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "D SCO-1898 \"Leila\"",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 13
  },
  {
    id: "lm-14",
    fileName: "LM_14.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AEoBdm0CIYiQE7UHtRpTU0A/LM_14.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "On the wet road by night",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 14
  },
  {
    id: "lm-15",
    fileName: "LM_15.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AB4dRIxOu9y3XRG68gdz3Go/LM_15.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Nocturn Op. 15 No. 2 (F.Chopin) (Homage to my mother)",
    year: "2004",
    size: "100x70cm",
    technic: "Oil on canvas",
    order: 15
  },
  {
    id: "lm-16",
    fileName: "LM_16.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AMmN9y44ofHZwYywMObyG0g/LM_16.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "When the sky meets the earth",
    year: "2004",
    size: "100x80cm",
    technic: "Oil on canvas",
    order: 16
  },
  {
    id: "lm-17",
    fileName: "LM_17.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AJBhQhNoDBLwl4zxBhNDRl4/LM_17.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "Requiem (L. Cherubini)",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 17
  },
  {
    id: "lm-18",
    fileName: "LM_18.jpg",
    url: "https://www.dropbox.com/scl/fo/acone7bw6ya33017uhwe6/AGKFPwcajNYZINAIpPhagKY/LM_18.jpg?rlkey=1cbcb23ua22uk88n5uct8gnrr&raw=1",
    paintingName: "The Grand Wazoo (F. Zappa)",
    year: "2004",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 18
  }
];

// Sur la terre comme au ciel artworks with metadata from Google Sheets
const SUR_LA_TERRE_ARTWORKS = [
  {
    id: "sltcac-01",
    fileName: "SLTCAC_01.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AJUjdCYXNEiiY9Ff1dEL_SA/SLTCAC_01.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Nature Blanche",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 1
  },
  {
    id: "sltcac-02",
    fileName: "SLTCAC_02.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AJnbaPiFsXfN9K-OUhGopPc/SLTCAC_02.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Vitam Impedre Vero (Consacrer sa vie au vrai)",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 2
  },
  {
    id: "sltcac-03",
    fileName: "SLTCAC_03.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AOs_GNqcydW5SW5lTAr4IwE/SLTCAC_03.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Noces Roses",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 3
  },
  {
    id: "sltcac-04",
    fileName: "SLTCAC_04.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AJlU8HNdxnYfI5zqVF_W2M8/SLTCAC_04.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Tell Me The Truth",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 4
  },
  {
    id: "sltcac-05",
    fileName: "SLTCAC_05.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/ACWyLWAGVmnFK1hpYRj2p8Q/SLTCAC_05.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Paradis Céleste",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 5
  },
  {
    id: "sltcac-06",
    fileName: "SLTCAC_06.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/ABWmXNV42UgCHmQGw2qYFhs/SLTCAC_06.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Dynamique des Herbes Folles",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 6
  },
  {
    id: "sltcac-07",
    fileName: "SLTCAC_07.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/ABhqTH0Z10G0Wvte5O76SJk/SLTCAC_07.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Résonances Colorées",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 7
  },
  {
    id: "sltcac-08",
    fileName: "SLTCAC_08.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AE-8lWIB-Ot8F8HiN3-dtDo/SLTCAC_08.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Rencontre Insolite",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 8
  },
  {
    id: "sltcac-09",
    fileName: "SLTCAC_09.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/ANgH7yqA4emycS-0MZfHHpQ/SLTCAC_09.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Paradoxalement, ce n'est pas !!!!!",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 9
  },
  {
    id: "sltcac-10",
    fileName: "SLTCAC_10.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/ABEbGHhLzoCN2PaBtra99yA/SLTCAC_10.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "Eaux Rebelles",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 10
  },
  {
    id: "sltcac-11",
    fileName: "SLTCAC_11.jpg",
    url: "https://www.dropbox.com/scl/fo/3atj6dfjaj7gau3z07tv1/AMH5YXtSZWJHjAgW0b653VQ/SLTCAC_11.jpg?rlkey=tc24inw7k8a5zswlja02v2h51&raw=1",
    paintingName: "En Eaux Profondes",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 11
  }
];

// Permanence et mouvement artworks with metadata from Google Sheets
const PERMANENCE_ET_MOUVEMENT_ARTWORKS = [
  {
    id: "pm-04",
    fileName: "PM_04.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AHeRYw2pLRKknFoDdJlG408/PM_04.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Permanence et mouvement",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "pm-01",
    fileName: "PM_01.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AFJD-meQ_441wOyLrmu4u1M/PM_01.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Rythmique du geste",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "pm-02",
    fileName: "PM_02.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AM28EdAQL8Ku2_naLrvc3Tc/PM_02.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Graphologie du peintre",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "pm-03",
    fileName: "PM_03.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AL-LF5Dx7b9bo5gC7z7jBho/PM_03.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Ivresse choréographique",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "pm-08",
    fileName: "PM_08.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AGKAuYtqTtv3x3EWSPZIDL4/PM_08.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Emancipation constructive",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "pm-14",
    fileName: "PM_14.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AKl_Kl4rP65UfxOdffsoPmg/PM_14.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Fantaisie oblique",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 6
  },
  {
    id: "pm-12",
    fileName: "PM_12.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AKNuBfVgAmIOAZHYDBkay6E/PM_12.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Czardas (Danse Hongroise)",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 7
  },
  {
    id: "pm-09",
    fileName: "PM_09.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AH8WPjiwBTnXTl-FHe5-1ME/PM_09.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Graphie",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 8
  },
  {
    id: "pm-10",
    fileName: "PM_10.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AIlVP_EaMRdTu4FIEhHBVaE/PM_10.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Osmose du trait",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 9
  },
  {
    id: "pm-05",
    fileName: "PM_05.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AFDR16A4eur-09xGOlmxHT8/PM_05.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Champ libre rose",
    year: "2010",
    size: "80x40cm",
    technic: "Oil on canvas",
    order: 10
  },
  {
    id: "pm-06",
    fileName: "PM_06.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/APVVQ9BhHxtT6momV22qXlU/PM_06.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Champ libre mauve",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 11
  },
  {
    id: "pm-07",
    fileName: "PM_07.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AIpTAI9TgxHvirZT9fMiq1o/PM_07.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Champ libre",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 12
  },
  {
    id: "pm-11",
    fileName: "PM_11.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AKbmHjTvXPy9E11bGkLo0R8/PM_11.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Caprice polymorphe",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 13
  },
  {
    id: "pm-13",
    fileName: "PM_13.jpg",
    url: "https://www.dropbox.com/scl/fo/itzphyftn2fxxcvzeo8ok/AOXcp5MS5wAjQobzdF43hrg/PM_13.jpg?rlkey=r64jo5cy2nwbu9vlcsnj0y0w5&raw=1",
    paintingName: "Cristallisation émotionelle",
    year: "2010",
    size: "92x73cm",
    technic: "Oil on canvas",
    order: 14
  }
];

// Série Vertes artworks with metadata from Google Sheets
const SERIE_VERTES_ARTWORKS = [
  {
    id: "sv-01",
    fileName: "SV_01.JPG",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AMcIRURT_mYQKRpVx7R-Hqg/SV_01.JPG?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Émancipation constructive II",
    year: "2023",
    size: "92x73cm",
    technic: "Huile sur toile",
    order: 1
  },
  {
    id: "sv-02",
    fileName: "SV_02.JPG",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AKM42zlZokTltJfLqYZkNDU/SV_02.JPG?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Cristallisation émotionelle",
    year: "2024",
    size: "100x81cm",
    technic: "Huile sur toile",
    order: 2
  },
  {
    id: "sv-03",
    fileName: "SV_03.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/ABdjeP8LUQZl7ue1nEumRik/SV_03.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Tourbillon",
    year: "2024",
    size: "92x73cm",
    technic: "Huile sur toile",
    order: 3
  },
  {
    id: "sv-04",
    fileName: "SV_04.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AA0nw7qNt4QO8Tqwm3I9PzU/SV_04.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Les algues vertes",
    year: "2014",
    size: "120x120cm",
    technic: "Huile sur toile",
    order: 4
  },
  {
    id: "sv-05",
    fileName: "SV_05.JPG",
    url: "https://www.dropbox.com/scl/fi/wu4kzqal3aizmbsmys65j/SV_05.JPG?rlkey=pt5fmf0aeo2z1uwqemmipehqz&raw=1",
    paintingName: "Vert intense",
    year: "2010",
    size: "116x90cm",
    technic: "Huile sur toile",
    order: 5
  },
  {
    id: "sv-06",
    fileName: "SV_06.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AL63yFr6yEV-rYXFlQjQL2Y/SV_06.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Vitam impedere vero",
    year: "2008",
    size: "120x120cm",
    technic: "Huile sur toile",
    order: 6
  },
  {
    id: "sv-07",
    fileName: "SV_07.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AN6QgYOt7HT1voZY90zv6Vs/SV_07.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Green Composition (\"The 6 Cello Suites\" J.S. Bach)",
    year: "2003",
    size: "116x90cm",
    technic: "Huile sur toile",
    order: 7
  },
  {
    id: "sv-08",
    fileName: "SV_08.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/ADoOJ8gGGl_X0k1vcqlBPuA/SV_08.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Pacific's sunrise",
    year: "2004",
    size: "120x100cm",
    technic: "Huile sur toile",
    order: 8
  },
  {
    id: "sv-09",
    fileName: "SV_09.jpg",
    url: "https://www.dropbox.com/scl/fo/a897acf3kmh151mdtymak/AKWhurNMMVNa2P1A6gG25iU/SV_09.jpg?rlkey=vyzk0mtt0jdxgo2g3sfwqzxk8&raw=1",
    paintingName: "Requiem (L. Cherubini)",
    year: "2004",
    size: "100x100cm",
    technic: "Huile sur toile",
    order: 9
  }
];

// Série Rouges artworks with metadata from Google Sheets
const SERIE_ROUGES_ARTWORKS = [
  {
    id: "sro-01",
    fileName: "SR_01.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/ACcxauONycEvg9M5H6Vf-l4/SR_01.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Chine Rouge I",
    year: "2010",
    size: "81x130cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "sro-02",
    fileName: "SR_02.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/APOhAfzwviNEDYj4QMtoUFE/SR_02.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Chine Rouge II",
    year: "2010",
    size: "81x130cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "sro-08",
    fileName: "SR_08.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/AADXdnQKtjYTzI3rJr34kC8/SR_08.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Peine de mort",
    year: "2002",
    size: "70x100cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "sro-06",
    fileName: "SR_06.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/AHJSjo5wo8F-MZ3HFkpeoWQ/SR_06.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Faux polygones",
    year: "2004",
    size: "100x120cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "sro-09",
    fileName: "SR_09.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/AH3YWS3Rouj8O-n1UBNexeQ/SR_09.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "My favorite things (John Coltrane)",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "sro-07",
    fileName: "SR_07.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/AEZWSLR9gNRUqrPywa41BBQ/SR_07.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Composition Rouge",
    year: "2009",
    size: "60x69cm",
    technic: "Oil on canvas",
    order: 6
  },
  {
    id: "sro-03",
    fileName: "SR_03.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/ALj3a-ebFIV6KZBlpXtgBdY/SR_03.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Rêve I",
    year: "2008",
    size: "50x50cm",
    technic: "Oil on canvas",
    order: 7
  },
  {
    id: "sro-04",
    fileName: "SR_04.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/ALPn4uoLD2QYCeFrtFVct5c/SR_04.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "Rêve II",
    year: "2008",
    size: "100x120cm",
    technic: "Oil on canvas",
    order: 8
  },
  {
    id: "sro-05",
    fileName: "SR_05.jpg",
    url: "https://www.dropbox.com/scl/fo/edfas4kj2wk3fjmxi7e35/AG0Ofq81w8gVaMpLxWonEaY/SR_05.jpg?rlkey=qj1lrvae2djph41ffvroqm1rp&raw=1",
    paintingName: "La danseuse étourdie",
    year: "2000",
    size: "69x71cm",
    technic: "Oil on canvas",
    order: 9
  },
];

// Série Bleues artworks with metadata from Google Sheets
const SERIE_BLEUES_ARTWORKS = [
  {
    id: "sb-01",
    fileName: "SB_01.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AK083bbqv5AeQs58L2vDOLU/SB_01.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Abysse",
    year: "2007",
    size: "100x120cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "sb-02",
    fileName: "SB_02.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AD6hU5fg3BzAIVEV3l-8Xxk/SB_02.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Zen Bleu",
    year: "2020",
    size: "100x100cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "sb-03",
    fileName: "SB_03.JPG",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AI63IQxw6yZtYPDXebNisNI/SB_03.JPG?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "La ville engloutie",
    year: "2010",
    size: "120x120",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "sb-04",
    fileName: "SB_04.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/ANfyOc8A5FklB8_cBSa9K78/SB_04.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Abysse en eaux profondes",
    year: "2010",
    size: "130x81cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "sb-05",
    fileName: "SB_05.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AKrWOk6kNitDgBtuopAaTu0/SB_05.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Nocturne",
    year: "2010",
    size: "61x76cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "sb-06",
    fileName: "SB_06.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AHNqVm-KawtYJjw018bu_2E/SB_06.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Clé des champs",
    year: "2010",
    size: "100x100",
    technic: "Oil on canvas",
    order: 6
  },
  {
    id: "sb-07",
    fileName: "SB_07.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AALOTR7X59ZH_ud4OrE0XWg/SB_07.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Morning Music",
    year: "2003",
    size: "100x90cm",
    technic: "Oil on canvas",
    order: 7
  },
  {
    id: "sb-08",
    fileName: "SB_08.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AF611o2HRNx0C5Be8r4x3dk/SB_08.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Résonances Colorées",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 8
  },
  {
    id: "sb-09",
    fileName: "SB_09.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AFOT7Og65JQgC7XAFqadbN4/SB_09.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Rencontre Insolite",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 9
  },
  {
    id: "sb-10",
    fileName: "SB_10.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/ACtvNXD_wSxFIeNKZXhT2BM/SB_10.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Paradoxalement, ce n'est pas !!!!!",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 10
  },
  {
    id: "sb-11",
    fileName: "SB_11.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/APS7vt9Koc7EkZlZukX5xO8/SB_11.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Eaux Rebelles",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 11
  },
  {
    id: "sb-12",
    fileName: "SB_12.jpg",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AF7UFt8phn9hnFSQTXgAKzg/SB_12.jpg?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "En Eaux Profondes",
    year: "2008",
    size: "120x120cm",
    technic: "Oil on Linen Canvas",
    order: 12
  },
  {
    id: "sb-13",
    fileName: "SB_13.JPG",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/AEu2g6wKE94Fw5f0H0El7QM/SB_13.JPG?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Blue Blues",
    year: "2024",
    size: "103x103cm",
    technic: "Oil on canvas",
    order: 13
  },
  {
    id: "sb-14",
    fileName: "SB_14.JPG",
    url: "https://www.dropbox.com/scl/fo/jlnxx2sid7y8kpzjt9hj5/ALEivm1X5_hDEHMAVoR-bt0/SB_14.JPG?rlkey=ok1ni67dzmisepviif5n1cqsk&raw=1",
    paintingName: "Noces Lunaires I",
    year: "2024",
    size: "116x81cm",
    technic: "Oil on canvas",
    order: 14
  }
];

// Closoirs artworks (images only, no text metadata)
const CLOSOIRS_ARTWORKS = [
  {
    id: "closoirs-01",
    fileName: "Canard_1.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AO9z7O3RWlK1gVhJV56EIMU/Canard_1.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 1
  },
  {
    id: "closoirs-02",
    fileName: "IMG_2273.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/APKDiJN9JGEp2AOcdRon7hI/IMG_2273.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 2
  },
  {
    id: "closoirs-03",
    fileName: "IMG_2285.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AJmskgEbQk-HCyT0MBAUEHc/IMG_2285.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 3
  },
  {
    id: "closoirs-04",
    fileName: "IMG_2311.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AItbXVkgK-Vw8hNbNp9qWWQ/IMG_2311.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 4
  },
  {
    id: "closoirs-05",
    fileName: "IMG_2317.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AOyo8rt9jQ21e38isAoeEOY/IMG_2317.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 5
  },
  {
    id: "closoirs-06",
    fileName: "IMG_2321.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/APdD2bH5bG_oRXFKoCsF4hU/IMG_2321.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 6
  },
  {
    id: "closoirs-07",
    fileName: "IMG_2324.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AK_PxjeBu0nPdw1-fHOhZQE/IMG_2324.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 7
  },
  {
    id: "closoirs-08",
    fileName: "IMG_2325.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/ALSgHLvejOAKx82yzsx6tmM/IMG_2325.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 8
  },
  {
    id: "closoirs-09",
    fileName: "IMG_2331.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/ABdZ5F9bjZvI9N9aGjS1DUc/IMG_2331.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 9
  },
  {
    id: "closoirs-10",
    fileName: "IMG_2332.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AAiiqxQgh9JSv3_1i5fl-uE/IMG_2332.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 10
  },
  {
    id: "closoirs-11",
    fileName: "IMG_2334.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AERqSNagg8T3aONevkdGxPY/IMG_2334.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 11
  },
  {
    id: "closoirs-12",
    fileName: "IMG_2335.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/ANOQbaea9OS8-rgKGTKdmBs/IMG_2335.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 12
  },
  {
    id: "closoirs-13",
    fileName: "MiniDragon_1.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AH8GRnFxygge_eCtk4BeWp0/MiniDragon_1.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 13
  },
  {
    id: "closoirs-14",
    fileName: "MiniDragon_2.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AJjMO3wYK9TP8ql8VqUMpkQ/MiniDragon_2.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 14
  },
  {
    id: "closoirs-15",
    fileName: "MiniDragon_3.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/ACiXo0YBhFKby7fY3sovqng/MiniDragon_3.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 15
  },
  {
    id: "closoirs-16",
    fileName: "Oiseaux_1.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AKE5DSLujJmkrYmN35-nJls/Oiseaux_1.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 16
  },
  {
    id: "closoirs-17",
    fileName: "Oiseaux_2.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/ABfzSyw2OksURBWUXKXCSGQ/Oiseaux_2.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 17
  },
  {
    id: "closoirs-18",
    fileName: "Oiseaux_3.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AM4db_3Z28KQwNWd5C11WDg/Oiseaux_3.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 18
  },
  {
    id: "closoirs-19",
    fileName: "PouleAncienne_1.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AHhH1LZIHDEAOl4-JBFSCr0/PouleAncienne_1.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 19
  },
  {
    id: "closoirs-20",
    fileName: "PouleAncienne_2.JPG",
    url: "https://www.dropbox.com/scl/fo/t033dckzblr8es52lhhas/AIck901wjS87NurRZACDc10/PouleAncienne_2.JPG?rlkey=wmua30da94c1qu5m6xgl00zfc&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 20
  }
];

// Aventures de Yellow artworks - 18 images with Dropbox share links (raw=1 format)
const AVENTURES_DE_YELLOW_ARTWORKS = [
  {
    id: "yellow-01",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AD5DoQVdGorqSdmLqvvFjo8/IMG_2000.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 1
  },
  {
    id: "yellow-02",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AHqxRZ6MN757e7ZX_USJ94g/IMG_2001.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 2
  },
  {
    id: "yellow-03",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AH0ZeXuxmB_4q6Ay6bGk_j8/IMG_2992.png?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 3
  },
  {
    id: "yellow-04",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AHlNc99vaOqc8GxI_nq0xiE/IMG_3008.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 4
  },
  {
    id: "yellow-05",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AJoaXNsooc--RaW2Z4eXrlQ/IMG_3038.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 5
  },
  {
    id: "yellow-06",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AIN2rLfwh78SGB1-MWsJmCA/IMG_3050.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 6
  },
  {
    id: "yellow-07",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/ALwP2MnRoDgMwCTonVAtSAw/IMG_3077.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 7
  },
  {
    id: "yellow-08",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AGR40X6leBq1syK_m7MxFQo/IMG_3078.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 8
  },
  {
    id: "yellow-09",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/ADKLNV4FR8Z9KOdacs6DhT8/IMG_3079.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 9
  },
  {
    id: "yellow-10",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/APoIo-p-4OO2e8xmkb-d4vM/IMG_3083.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 10
  },
  {
    id: "yellow-11",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AAxVwPCgarDnnQTS_addCpU/IMG_3087.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 11
  },
  {
    id: "yellow-12",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AC2NsTUCnOAEigNvAyXhSFQ/IMG_3097.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 12
  },
  {
    id: "yellow-13",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AKhcJ180ckO7YRzp212eL7o/IMG_3100.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 13
  },
  {
    id: "yellow-14",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/ALkGf6b74x7ggetZ5xjn5xg/IMG_3104.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 14
  },
  {
    id: "yellow-15",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AM4rwMfzganb_12OJ0h38-Q/IMG_3390.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 15
  },
  {
    id: "yellow-16",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AEj4QxrpRxWpc5dkuCdSzMc/IMG_3575.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 16
  },
  {
    id: "yellow-17",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/AKox8BgXuyc9QXAfybvIlGg/IMG_3800.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 17
  },
  {
    id: "yellow-18",
    url: "https://www.dropbox.com/scl/fo/ild8tp1dva6podqvicw4x/APMLxF3ORfG6lTuUu7uEiqA/IMG_3900.JPG?rlkey=cuqncki8re9pk7344lebbpsdx&raw=1",
    paintingName: "",
    year: "",
    size: "",
    technic: "",
    order: 18
  }
];

// Série Rose artworks with metadata from Google Sheets
const SERIE_ROSE_ARTWORKS = [
  {
    id: "srm-01",
    fileName: "SRM_01.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AG_vkOmTAvWpec5_edGe7R8/SRM_01.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Noces Roses",
    year: "2008",
    size: "120x120cm",
    technic: "Huile sur toile",
    order: 1
  },
  {
    id: "srm-02",
    fileName: "SRM_02.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AN7mZGWmmCnFxn8aeixgRlc/SRM_02.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Ode à la vie",
    year: "2020",
    size: "100x100cm",
    technic: "Huile sur toile",
    order: 2
  },
  {
    id: "srm-03",
    fileName: "SRM_03.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AFgu8uUeOgijNDW5q4ZiKcM/SRM_03.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Dynamique des Herbes Folles",
    year: "2008",
    size: "120x120cm",
    technic: "Huile sur toile",
    order: 3
  },
  {
    id: "srm-04",
    fileName: "SRM_04.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AAiQWN2XIflw5v0yKescy9U/SRM_04.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Petite soie",
    year: "2010",
    size: "100x100cm",
    technic: "Huile sur toile",
    order: 4
  },
  {
    id: "srm-05",
    fileName: "SRM_05.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/APLpqytWtrl5xliXWXYrGK8/SRM_05.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Abstraction mauve",
    year: "2012",
    size: "90x70cm",
    technic: "Huile sur toile",
    order: 5
  },
  {
    id: "srm-06",
    fileName: "SRM_06.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AMkU20qO1rn7BZBhtl7g7Y4/SRM_06.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Clé des champs",
    year: "2011",
    size: "100x100cm",
    technic: "Huile sur toile",
    order: 6
  },
  {
    id: "srm-07",
    fileName: "SRM_07.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/ADeZ9qWYtdK9bAaP0GSdF6U/SRM_07.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Champ libre rose",
    year: "2010",
    size: "80x40cm",
    technic: "Huile sur toile",
    order: 7
  },
  {
    id: "srm-08",
    fileName: "SRM_08.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AMwYGD-CPcqKVMPN7bEr394/SRM_08.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Ivresse chorégraphique",
    year: "2010",
    size: "92x73cm",
    technic: "Huile sur toile",
    order: 8
  },
  {
    id: "srm-09",
    fileName: "SRM_09.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/ANc1Zs8EsyZDkwwUrlmhVBs/SRM_09.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Champ libre",
    year: "2010",
    size: "92x73cm",
    technic: "Huile sur toile",
    order: 9
  },
  {
    id: "srm-10",
    fileName: "SRM_10.jpg",
    url: "https://www.dropbox.com/scl/fo/255byp9gu776endw46rkr/AAb5Wg4Re9UhKD48gEM3c7U/SRM_10.jpg?rlkey=4w14lpwqbg7i8wuz9i3kwawub&raw=1",
    paintingName: "Fragmentation mauve",
    year: "2013",
    size: "100x100cm",
    technic: "Huile sur toile",
    order: 10
  }
];

// Blue Blues artworks with metadata from Google Sheets
// Rêves bleus artworks (2025 collection)
const REVES_BLEUS_ARTWORKS = [
  {
    id: "rb-01",
    fileName: "RB_01.jpeg",
    url: "/images/reves_bleus/RB_01.jpeg",
    paintingName: "Métaphormose de Thétis",
    year: "2025",
    size: "132x89cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "rb-02",
    fileName: "RB_02.jpg",
    url: "/images/reves_bleus/RB_02.jpg",
    paintingName: "L'Intruse 1",
    year: "2025",
    size: "105x82cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "rb-03",
    fileName: "RB_03.jpg",
    url: "/images/reves_bleus/RB_03.jpg",
    paintingName: "L'Intruse 2",
    year: "2025",
    size: "105x82cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "rb-04",
    fileName: "RB_04.jpeg",
    url: "/images/reves_bleus/RB_04.jpeg",
    paintingName: "Dilution",
    year: "2025",
    size: "105x82cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "rb-05",
    fileName: "RB_05.png",
    url: "/images/reves_bleus/RB_05.png",
    paintingName: "Blue de la Paix",
    year: "2025",
    size: "77x62cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "rb-06",
    fileName: "RB_06.jpg",
    url: "/images/reves_bleus/RB_06.jpg",
    paintingName: "Vibrations aquatiques",
    year: "2025",
    size: "83x74cm",
    technic: "Oil on canvas",
    order: 6
  }
];

const BLUE_BLUES_ARTWORKS = [
  {
    id: "bb-01",
    fileName: "BB_01.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/AOshSMqe-QQKs3SU4CJosj4/BB_01.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Blue Blues",
    year: "2024",
    size: "103x103cm",
    technic: "Oil on canvas",
    order: 1
  },
  {
    id: "bb-02",
    fileName: "BB_02.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/AA9ckWqV705uEbtxONmyxg8/BB_02.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Blue Moons",
    year: "2024",
    size: "116x81cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "bb-03",
    fileName: "BB_03.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/ANQe3civmcoU13gPOguHZOo/BB_03.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Noces Lunaires I",
    year: "2024",
    size: "102x82cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "bb-04",
    fileName: "BB_04.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/AFagJocwT0GOKOV_u8eojnk/BB_04.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Cristallisation Émotionnelle",
    year: "2024",
    size: "100x81cm",
    technic: "Oil on canvas",
    order: 4
  },
  {
    id: "bb-05",
    fileName: "BB_05.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/AGH__XSCXhMi6uuIro04zfY/BB_05.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Noces Lunaires II",
    year: "2024",
    size: "116x81cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "bb-06",
    fileName: "BB_06.JPG",
    url: "https://www.dropbox.com/scl/fo/gz3lhikzqc74x5pqt2b8e/AIeaEnqtk1Up7o6yzoKz2V0/BB_06.JPG?rlkey=j1hz7v7mlbkhw4et5dfmikxl0&raw=1",
    paintingName: "Mélancolie",
    year: "2024",
    size: "100x81cm",
    technic: "Oil on canvas",
    order: 6
  }
];

// Actualité et abstractions artworks with metadata from Google Sheets
const ACTUALITE_ET_ABSTRACTIONS_ARTWORKS = [
  {
    id: "aa-01",
    fileName: "AA_01.jpg",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/AI5vuclFmPZs58rns4pWZ3w/AA_01.jpg?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "Death Penalty",
    year: "2002",
    size: "45x40cm",
    technic: "Oil on canvas + Collage",
    order: 1
  },
  {
    id: "aa-02",
    fileName: "AA_02.jpg",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/ANYvLhO8uCYeuXEbsLIdZQQ/AA_02.jpg?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "Etude 9/11",
    year: "2002",
    size: "40x60cm",
    technic: "Oil on canvas",
    order: 2
  },
  {
    id: "aa-03",
    fileName: "AA_03.jpg",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/AMPKStGrPwyalcAGLGpFOWk/AA_03.jpg?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "La guerre de l'or noir",
    year: "1997",
    size: "100x120cm",
    technic: "Oil on canvas",
    order: 3
  },
  {
    id: "aa-04",
    fileName: "AA_04.jpg",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/ABP2U9CSAagl7tt4j-gCxjo/AA_04.jpg?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "Mariéé de Bagdad",
    year: "2003",
    size: "50x50cm",
    technic: "Oil on canvas + Collage",
    order: 4
  },
  {
    id: "aa-05",
    fileName: "AA_05.jpg",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/AKDGemkKlO4ihwB0N1uno_E/AA_05.jpg?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "Peine de mort",
    year: "2002",
    size: "70x100cm",
    technic: "Oil on canvas",
    order: 5
  },
  {
    id: "aa-06",
    fileName: "AA_06.JPG",
    url: "https://www.dropbox.com/scl/fo/nmy8zwrsczddta61ivmjx/ACrVNjmpMgazWYHD0Rln4Z0/AA_06.JPG?rlkey=u6jvjjrnve4a3pgwo729em9a1&raw=1",
    paintingName: "Les mains rouges",
    year: "2025",
    size: "100x100cm",
    technic: "Oil on canvas + mixed technic",
    order: 6
  },
  {
    id: "aa-07",
    fileName: "AA_07.jpg",
    url: "/images/actualite_abstractions/AA_07.jpg",
    paintingName: "Avalanche",
    year: "2026",
    size: "105x82cm",
    technic: "Oil on canvas",
    order: 7
  }
];

// Slideshow interval
const SLIDESHOW_INTERVAL = 8000;

// Header Component with language support and active state
const Header = ({ transparent = false, language = 'fr', onLanguageToggle, activeSection = '' }) => {
  const navigate = useNavigate();
  
  const menuLabels = {
    fr: {
      collections: 'Collections',
      exhibitions: 'Expo & Historique',
      bio: 'À propos',
      press: 'Presse',
      contact: 'Contact'
    },
    en: {
      collections: 'Collections',
      exhibitions: 'Exhibitions & Timeline',
      bio: 'About',
      press: 'Press',
      contact: 'Contact'
    }
  };
  
  const labels = menuLabels[language] || menuLabels.fr;
  
  return (
    <header className={`site-header ${transparent ? 'transparent' : ''}`} data-testid="site-header">
      <div 
        className="site-logo" 
        onClick={() => navigate("/")}
        data-testid="site-logo"
      >
        Elisabeth Troestler
      </div>
      <nav className="site-nav" data-testid="site-nav">
        <Link to="/collections" className={`nav-link ${activeSection === 'collections' ? 'active' : ''}`}>{labels.collections}</Link>
        <Link to="/exhibitions" className={`nav-link ${activeSection === 'exhibitions' ? 'active' : ''}`}>{labels.exhibitions}</Link>
        <Link to="/bio" className={`nav-link ${activeSection === 'bio' ? 'active' : ''}`}>{labels.bio}</Link>
        <Link to="/contact" className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}>{labels.contact}</Link>
        {onLanguageToggle && (
          <button 
            className="nav-language-toggle"
            onClick={onLanguageToggle}
            data-testid="nav-language-toggle"
          >
            {language === 'fr' ? 'English' : 'Français'}
          </button>
        )}
      </nav>
    </header>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState([false, false, false, false, false]);
  const navigate = useNavigate();

  // Alternate button text between French and English with each slide
  const buttonTexts = ['Entrer', 'Enter', 'Entrer', 'Enter', 'Entrer'];
  const currentButtonText = buttonTexts[currentIndex % buttonTexts.length];

  useEffect(() => {
    LANDING_IMAGES.forEach((img, index) => {
      const image = new Image();
      image.onload = () => {
        setImagesLoaded(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      };
      image.src = img.url;
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % LANDING_IMAGES.length);
        setIsTransitioning(false);
      }, 1000);
    }, SLIDESHOW_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const handleEnter = useCallback(() => {
    navigate("/collections");
  }, [navigate]);

  const allImagesLoaded = imagesLoaded.every(Boolean);

  return (
    <div className="landing-page" data-testid="landing-page">
      <PageSEO page="home" />
      <div className="slideshow-container">
        {LANDING_IMAGES.map((image, index) => (
          <div
            key={index}
            className={`slideshow-image ${
              index === currentIndex ? "active" : ""
            } ${isTransitioning && index === currentIndex ? "fading" : ""}`}
            style={{ backgroundImage: `url(${image.url})` }}
            role="img"
            aria-label={image.alt}
          />
        ))}
      </div>

      <div className="landing-overlay">
        <div className="landing-content">
          <h1 className="artist-name" data-testid="artist-name">
            Elisabeth Troestler
          </h1>
          <p className="artist-subtitle">Painter</p>
          <button
            className="enter-button"
            onClick={handleEnter}
            data-testid="enter-button"
          >
            {currentButtonText}
          </button>
        </div>
      </div>

      {!allImagesLoaded && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

// Artwork Modal Component
const ArtworkModal = ({ artwork, artworks, onClose, onPrev, onNext }) => {
  const currentIndex = artworks.findIndex(a => a.id === artwork.id);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="artwork-modal" onClick={onClose} data-testid="artwork-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="modal-close">
          ×
        </button>
        
        {/* Desktop navigation - positioned on sides */}
        <button 
          className="modal-nav modal-prev modal-nav-desktop" 
          onClick={onPrev}
          disabled={currentIndex === 0}
          data-testid="modal-prev-desktop"
        >
          ‹
        </button>
        
        <div className="modal-image-container">
          <img 
            src={artwork.url} 
            alt={artwork.paintingName}
            className="modal-image"
          />
        </div>
        
        {/* Desktop navigation - positioned on sides */}
        <button 
          className="modal-nav modal-next modal-nav-desktop" 
          onClick={onNext}
          disabled={currentIndex === artworks.length - 1}
          data-testid="modal-next-desktop"
        >
          ›
        </button>
        
        <div className="modal-info">
          <h3 className="modal-title">{artwork.paintingName}</h3>
          <p className="modal-details">
            {artwork.year && `${artwork.year}`}
            {artwork.year && artwork.size && " · "}
            {artwork.size}
            {(artwork.year || artwork.size) && artwork.technic && " · "}
            {artwork.technic}
          </p>
        </div>
        
        {/* Mobile navigation row - below the text */}
        <div className="modal-navigation-row">
          <button 
            className="modal-nav modal-nav-mobile" 
            onClick={onPrev}
            disabled={currentIndex === 0}
            data-testid="modal-prev-mobile"
          >
            ‹ Précédent
          </button>
          <span className="modal-counter">{currentIndex + 1} / {artworks.length}</span>
          <button 
            className="modal-nav modal-nav-mobile" 
            onClick={onNext}
            disabled={currentIndex === artworks.length - 1}
            data-testid="modal-next-mobile"
          >
            Suivant ›
          </button>
        </div>
      </div>
    </div>
  );
};

// Collections Overview Component - Shows all collections as a visual grid
const CollectionsOverview = ({ language, onLanguageToggle }) => {
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);

  const handleImageLoad = (id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  };

  const handleCardClick = (slug) => {
    navigate(`/collections/${slug}`);
  };

  const handleCardHover = (id) => {
    setExpandedCard(id);
  };

  const handleCardLeave = () => {
    setExpandedCard(null);
  };

  return (
    <div className="collections-overview-page" data-testid="collections-overview-page">
      <PageSEO page="collections" />
      <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="collections" />
      
      <main className="collections-overview-content">
        <h1 className="collections-overview-title">Collections</h1>
        
        <div className="collections-overview-grid">
          {COLLECTION_THUMBNAILS.map(collection => (
            <div 
              key={collection.id}
              className={`collection-overview-card ${expandedCard === collection.id ? 'expanded' : ''}`}
              onClick={() => handleCardClick(collection.slug)}
              onMouseEnter={() => handleCardHover(collection.id)}
              onMouseLeave={handleCardLeave}
              data-testid={`collection-overview-${collection.slug}`}
            >
              <div className="collection-overview-image-wrapper">
                {!loadedImages[collection.id] && (
                  <div className="collection-overview-placeholder">
                    <div className="loading-spinner small"></div>
                  </div>
                )}
                <img 
                  src={collection.url} 
                  alt={collection.name}
                  className={`collection-overview-image ${loadedImages[collection.id] ? 'loaded' : ''}`}
                  onLoad={() => handleImageLoad(collection.id)}
                />
              </div>
              <div className="collection-overview-info">
                <h3 className="collection-overview-name">{collection.name}</h3>
                <p className="collection-overview-details">
                  {collection.year && `${collection.year}`}
                  {collection.year && collection.technic && " · "}
                  {collection.technic}
                </p>
              </div>
              
              {/* Expanded preview overlay */}
              {expandedCard === collection.id && (
                <div className="collection-expanded-overlay">
                  <div className="collection-expanded-image-container">
                    <img 
                      src={collection.url} 
                      alt={collection.name}
                      className="collection-expanded-image"
                    />
                  </div>
                  <div className="collection-expanded-info">
                    <h3>{collection.name}</h3>
                    <p>
                      {collection.year && `${collection.year}`}
                      {collection.year && collection.technic && " · "}
                      {collection.technic}
                    </p>
                    <span className="view-collection-hint">{language === 'fr' ? 'Voir la collection' : 'View collection'}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// Collections Page Component (shows individual collection with sidebar)
const CollectionsPage = ({ language, onLanguageToggle }) => {
  const { collectionSlug } = useParams();
  const navigate = useNavigate();
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [themesExpanded, setThemesExpanded] = useState(false);
  const [seriesExpanded, setSeriesExpanded] = useState(false);
  
  // If no slug provided, redirect to overview
  if (!collectionSlug) {
    return <CollectionsOverview language={language} onLanguageToggle={onLanguageToggle} />;
  }
  
  // Get current collection
  const currentCollection = COLLECTIONS.find(c => c.slug === collectionSlug);
  
  // If collection not found, redirect to overview
  if (!currentCollection) {
    return <CollectionsOverview language={language} onLanguageToggle={onLanguageToggle} />;
  }
  
  // Get artworks for current collection
  const getArtworksForCollection = (slug) => {
    if (slug === 'reves-bleus') {
      return REVES_BLEUS_ARTWORKS;
    }
    if (slug === 'blue-blues') {
      return BLUE_BLUES_ARTWORKS;
    }
    if (slug === 'serie-rose') {
      return SERIE_ROSE_ARTWORKS;
    }
    if (slug === 'serie-bleues') {
      return SERIE_BLEUES_ARTWORKS;
    }
    if (slug === 'serie-vertes') {
      return SERIE_VERTES_ARTWORKS;
    }
    if (slug === 'serie-rouges') {
      return SERIE_ROUGES_ARTWORKS;
    }
    if (slug === 'closoirs') {
      return CLOSOIRS_ARTWORKS;
    }
    if (slug === 'aventures-de-yellow') {
      return AVENTURES_DE_YELLOW_ARTWORKS;
    }
    if (slug === 'line-and-music') {
      return LINE_AND_MUSIC_ARTWORKS;
    }
    if (slug === 'sur-la-terre-comme-au-ciel') {
      return SUR_LA_TERRE_ARTWORKS;
    }
    if (slug === 'permanence-et-mouvement') {
      return PERMANENCE_ET_MOUVEMENT_ARTWORKS;
    }
    if (slug === 'actualite-et-abstractions') {
      return ACTUALITE_ET_ABSTRACTIONS_ARTWORKS;
    }
    return [];
  };
  
  const artworks = getArtworksForCollection(currentCollection.slug);

  const handleArtworkClick = (artwork) => {
    setSelectedArtwork(artwork);
  };

  const handleCloseModal = () => {
    setSelectedArtwork(null);
  };

  const handlePrevArtwork = () => {
    const currentIndex = artworks.findIndex(a => a.id === selectedArtwork.id);
    if (currentIndex > 0) {
      setSelectedArtwork(artworks[currentIndex - 1]);
    }
  };

  const handleNextArtwork = () => {
    const currentIndex = artworks.findIndex(a => a.id === selectedArtwork.id);
    if (currentIndex < artworks.length - 1) {
      setSelectedArtwork(artworks[currentIndex + 1]);
    }
  };

  const handleImageLoad = (id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  };

  return (
    <div className="collections-page" data-testid="collections-page">
      <PageSEO page="collections" />
      <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="collections" />
      
      <div className="collections-layout">
        {/* Left Sidebar - Collection Names */}
        <aside className="collections-sidebar" data-testid="collections-sidebar">
          <h2 className="sidebar-title">Collections</h2>
          
          <div className="sidebar-section">
            <h3 className="sidebar-section-title">Thèmes</h3>
            <button 
              className={`sidebar-section-toggle ${themesExpanded ? 'expanded' : ''}`}
              onClick={() => setThemesExpanded(!themesExpanded)}
              aria-expanded={themesExpanded}
            >
              <span>Thèmes</span>
              <span className="toggle-icon">{themesExpanded ? '−' : '+'}</span>
            </button>
            <ul className={`collections-list collapsible ${themesExpanded ? 'expanded' : ''}`}>
              {THEMES_COLLECTIONS.map(collection => (
                <li key={collection.id}>
                  <Link
                    to={`/collections/${collection.slug}`}
                    className={`collection-link ${currentCollection.slug === collection.slug ? 'active' : ''}`}
                    data-testid={`collection-link-${collection.slug}`}
                  >
                    {collection.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="sidebar-section">
            <h3 className="sidebar-section-title">Séries par couleur</h3>
            <button 
              className={`sidebar-section-toggle ${seriesExpanded ? 'expanded' : ''}`}
              onClick={() => setSeriesExpanded(!seriesExpanded)}
              aria-expanded={seriesExpanded}
            >
              <span>Séries par couleur</span>
              <span className="toggle-icon">{seriesExpanded ? '−' : '+'}</span>
            </button>
            <ul className={`collections-list collapsible ${seriesExpanded ? 'expanded' : ''}`}>
              {SERIES_COLLECTIONS.map(collection => (
                <li key={collection.id}>
                  <Link
                    to={`/collections/${collection.slug}`}
                    className={`collection-link ${currentCollection.slug === collection.slug ? 'active' : ''}`}
                    data-testid={`collection-link-${collection.slug}`}
                  >
                    {collection.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
        
        {/* Right Content - Artworks Grid */}
        <main className="collections-content" data-testid="collections-content">
          <h1 className="collection-title">{currentCollection.name}</h1>
          {currentCollection.subtitle && (
            <p className="collection-subtitle">{currentCollection.subtitle}</p>
          )}
          
          <div className="artworks-grid">
            {artworks.map(artwork => (
              <div 
                key={artwork.id} 
                className="artwork-card"
                onClick={() => handleArtworkClick(artwork)}
                data-testid={`artwork-card-${artwork.id}`}
              >
                <div className="artwork-image-wrapper">
                  {!loadedImages[artwork.id] && (
                    <div className="artwork-placeholder">
                      <div className="loading-spinner small"></div>
                    </div>
                  )}
                  <img 
                    src={artwork.url} 
                    alt={artwork.paintingName}
                    className={`artwork-image ${loadedImages[artwork.id] ? 'loaded' : ''}`}
                    onLoad={() => handleImageLoad(artwork.id)}
                  />
                </div>
                <div className="artwork-info">
                  <h3 className="artwork-name">{artwork.paintingName}</h3>
                  <p className="artwork-details">
                    {artwork.year}
                    {artwork.year && artwork.size && " · "}
                    {artwork.size}
                    {(artwork.year || artwork.size) && artwork.technic && " · "}
                    {artwork.technic}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
      
      {/* Artwork Modal */}
      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork}
          artworks={artworks}
          onClose={handleCloseModal}
          onPrev={handlePrevArtwork}
          onNext={handleNextArtwork}
        />
      )}
    </div>
  );
};

// Artist portrait
const ARTIST_PORTRAIT = "https://www.dropbox.com/scl/fi/xw1sa9fzaumi1bwtxxnl4/IMG_2854.JPG?rlkey=b0t3tett9aie5wj5lbcomp7g2&raw=1";

// Bio mosaic images
const BIO_MOSAIC_IMAGES = [
  {
    id: "bio-1",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/ADPlJ5Ad0YkP07DH3Vvqy2I/IMG_2854.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler portrait"
  },
  {
    id: "bio-2",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/AJDrLzDJ0a_W7J3VruQbr7g/IMG_3860.jpg?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler"
  },
  {
    id: "bio-3",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/ALEc5Sh2adHbw8mBU_FbhgU/PICT2068.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler in studio"
  },
  {
    id: "bio-4",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/AP4_OprTywkf1EunoTpMht8/Presentation-007.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler artwork presentation"
  },
  {
    id: "bio-5",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/ALYkbwTp0uy0N5u_08jG8HA/Presentation-017.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler artwork"
  },
  {
    id: "bio-6",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/ACWkc0dyLssN5w5a78tsD7g/Presentation-049.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler artwork"
  },
  {
    id: "bio-7",
    url: "https://www.dropbox.com/scl/fo/2bcnqdflzihlkr1q9mt1j/AOBBPWjQZjZwmh5hmgIv1AI/Presentation-085.JPG?rlkey=17hbnma3fwaha75g57ojphlks&raw=1",
    alt: "Elisabeth Troestler artwork"
  }
];

// Bio content
const BIO_CONTENT = {
  fr: `J'ai toujours dessiné et utilisé l'encre, le crayon, l'aquarelle ou le pastel pour m'exprimer. J'ai commencé à étudier d'une façon plus académique en suivant des cours de dessin et de peinture à l'École des Beaux Arts de Bondi à Sydney en Australie de 1983 à 1988. J'ai complété ces cours par l'étude de la peinture chinoise.

La musique est aussi un ancrage dans ma vie. Qu'elle soit classique, moderne ou ethnique, sa présence m'inspire continuellement.

J'ai quitté la France en 1978. J'ai vécu successivement de longues périodes aux Etats-Unis, en Australie, en Norvège, au Nigéria et en Russie. Retour définitif en France en 2005.

Tous ces longs voyages m'ont transformée en nomade et les différents pays où j'ai vécu ont laissé une empreinte dans ma façon de peindre et d'utiliser les pigments et l'huile pour créer des mondes imaginaires et colorés.`,
  en: `I have always drawn and used ink, pencil, watercolor, or pastel to express myself. I began studying more academically by taking drawing and painting courses at the Bondi School of Fine Arts in Sydney, Australia, from 1983 to 1988. I complemented these courses with the study of Chinese painting.

Music is also an anchor in my life. Whether classical, modern, or ethnic, its presence continually inspires me.

I left France in 1978. I lived successively for long periods in the United States, Australia, Norway, Nigeria, and Russia. I returned definitively to France in 2005.

All these long journeys transformed me into a nomad, and the different countries where I lived have left an imprint on my way of painting and using pigments and oil to create imaginary and colorful worlds.`
};

// Exhibitions data organized by decade
const EXHIBITIONS = [
  // 2020s
  { year: "2026", title: "Salon des Artistes 2026", venue: "Théâtre des Sablons, Neuilly-Sur-Seine", country: "France", theme: "Série Rêves bleus 2026 (1-4 octobre 2026)", type: "salon" },
  { year: "2026", title: "Exposition au Théâtre des Sablons", venue: "Théâtre des Sablons, Neuilly-Sur-Seine", country: "France", theme: "Fulgurance mauve (26-31 mai 2026)", type: "salon" },
  { year: "2026", title: "Art en Capital", venue: "Grand Palais, Paris", country: "France", theme: "Noces Lunaires II (13-15 février 2026)", type: "salon" },
  { year: "2025", title: "Salon des Artistes Professionnels", venue: "Neuilly sur Seine", country: "France", theme: "Blue Blues", type: "solo" },
  { year: "2023-2024", title: "Préparation de peintures", venue: "", country: "", theme: "Blue Blues - Huiles sur toile", type: "preparation" },
  { year: "2021-2023", title: "Création de 50 « closoirs »", venue: "", country: "", theme: "Peintures sur bois à tempera et à l'huile", type: "creation" },
  { year: "2017-2020", title: "Étude de peintures anciennes sur bois", venue: "Édifices religieux et maisons seigneuriales du Languedoc", country: "France", theme: "XIVe au XVIIe siècle", type: "study" },
  { year: "2015", title: "Création de 200 collages", venue: "", country: "", theme: "Sujets événementiels (A4)", type: "creation" },
  // 2010s
  { year: "2013", title: "Who's Who International", venue: "Galerie 60, Giverny", country: "France", theme: "Huiles sur toile (abstractions)", type: "group" },
  { year: "2012", title: "Salon des Artistes contemporains", venue: "Honfleur", country: "France", theme: "« Légèreté verte » et série « Envolée lyrique »", type: "salon" },
  { year: "2011", title: "GO WEST - Exposition France-Texas", venue: "Houston (Williams Tower)", country: "USA", theme: "", type: "group" },
  { year: "2011", title: "GO WEST - UNESCO", venue: "Paris", country: "France", theme: "Dialogue culturel sur « la mémoire chez l'artiste »", type: "group" },
  { year: "2011", title: "Exposition collective Stella Art International", venue: "", country: "", theme: "", type: "group" },
  { year: "2011", title: "Lauréate - Prix Consécration 2011", venue: "Fédération Nationale de la Culture Française", country: "France", theme: "Série « Permanence et Mouvement »", type: "award" },
  { year: "2010", title: "Salon Art Shopping - Carrousel du Louvre", venue: "Paris", country: "France", theme: "Abstractions en rouge et noir", type: "salon" },
  { year: "2010", title: "Galerie Aroa", venue: "", country: "", theme: "« Permanence et Mouvement »", type: "solo" },
  { year: "2010", title: "Lauréate - Prix Consécration 2010", venue: "Fédération Nationale de la Culture Française", country: "France", theme: "« Vitam Impedere Vero »", type: "award" },
  // 2007-2011 Art en Capital
  { year: "2007-2011", title: "Art en Capital", venue: "Grand Palais, Paris", country: "France", theme: "Peintures à l'huile (grands formats)", type: "salon" },
  { year: "2009", title: "Portes ouvertes des ateliers", venue: "Musée Arturo Lopez, Neuilly sur Seine", country: "France", theme: "Série « Sur la terre comme au Ciel » (15 grands formats)", type: "open-studio" },
  { year: "2009", title: "Salon international du Val d'Or", venue: "Meillant", country: "France", theme: "Hommage à Christian Lacroix", type: "salon" },
  { year: "2009", title: "Galerie Everarts", venue: "Paris", country: "France", theme: "Peintures abstraites d'après collages", type: "group" },
  { year: "2009", title: "120ème Salon des Artistes Indépendants", venue: "Porte de Champerret, Paris", country: "France", theme: "« En Eaux profondes »", type: "salon" },
  { year: "2008", title: "Galerie Marie Beyer - Inauguration", venue: "Palais-Royal, Paris", country: "France", theme: "Diptyque: Chine Rouge", type: "solo" },
  { year: "2007", title: "Biennale de New-York", venue: "Reference Gallery N.Y.C, 137 Grand Street, New-York 10013", country: "USA", theme: "Commissaire d'Exposition pour la France: C. Marumo abstraites – cinq peintures grands formats", type: "biennial" },
  // 2000s
  { year: "2005", title: "Rétrospective de mon travail – dessins, collages, peintures", venue: "Musée de la Civilisation Française, Moscou", country: "Russie", theme: "Commissaire: Evguenia Sergueeva, chercheuse au Musée d'Art Moderne de Moscou", type: "retrospective" },
  { year: "2004", title: "ART PLAY GALLERY", venue: "Moscou", country: "Russie", theme: "« Line and Music » – Commissaire: Tatiana Paleeva – Exposition personnelle", type: "solo" },
  { year: "2002-2007", title: "Création de 200 collages A4", venue: "", country: "", theme: "Sujets divers avec yellow", type: "creation" },
  { year: "2003", title: "89ème Salon de la Peinture Française", venue: "Château de la Grenouillère", country: "France", theme: "Série « Petits bols chinois »", type: "salon" },
  { year: "2002", title: "37ème Salon du Vésinet", venue: "Le Vésinet", country: "France", theme: "« Death Penalty », « 9/11 », « Oklahoma City »", type: "salon" },
  { year: "2001", title: "36ème Salon des Peintres du Vésinet", venue: "Le Vésinet", country: "France", theme: "« La Danseuse Étourdie » - Premier Prix de la Ville", type: "award" },
  { year: "1999", title: "Huiles sur toile", venue: "", country: "", theme: "« La Ville Engloutie »", type: "creation" },
  { year: "1996", title: "Signature Gallery", venue: "Lagos", country: "Nigéria", theme: "", type: "solo" },
  { year: "1995", title: "Recherches sur le rythme et les courants marins", venue: "", country: "", theme: "", type: "research" },
  // 1990s
  { year: "1991", title: "Centre Culturel de Stavanger", venue: "Stavanger", country: "Norvège", theme: "« Pierres et Visages » - Pastels, huiles sur toile", type: "solo" },
  { year: "1990", title: "Galerie La Maison", venue: "Stavanger", country: "Norvège", theme: "« Masques et Miroirs » - Huiles sur toile", type: "solo" }
];

// Bio Page Component
const BioPage = ({ language, onLanguageToggle }) => {
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  };

  return (
    <div className="page-container" data-testid="bio-page">
      <PageSEO page="bio" />
      <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="bio" />
      <main className="bio-content">
        <div className="bio-layout">
          {/* Mosaic Gallery */}
          <div className="bio-mosaic-section">
            <div className="bio-mosaic-grid">
              {BIO_MOSAIC_IMAGES.map((image, index) => (
                <div 
                  key={image.id} 
                  className={`mosaic-item mosaic-item-${index + 1}`}
                >
                  {!loadedImages[image.id] && (
                    <div className="mosaic-placeholder">
                      <div className="loading-spinner small"></div>
                    </div>
                  )}
                  <img 
                    src={image.url} 
                    alt={image.alt}
                    className={`mosaic-image ${loadedImages[image.id] ? 'loaded' : ''}`}
                    onLoad={() => handleImageLoad(image.id)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Bio Text */}
          <div className="bio-text-section">
            <div className="bio-header">
              <h1 className="bio-title">Elisabeth Troestler</h1>
            </div>
            
            <div className="bio-statement" data-testid="bio-statement">
              {BIO_CONTENT[language].split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            
            <div className="bio-journey">
              <h3 className="journey-title">{language === 'fr' ? 'Parcours' : 'Journey'}</h3>
              <div className="journey-places">
                <span className="place">France</span>
                <span className="arrow">→</span>
                <span className="place">USA</span>
                <span className="arrow">→</span>
                <span className="place">{language === 'fr' ? 'Australie' : 'Australia'}</span>
                <span className="arrow">→</span>
                <span className="place">{language === 'fr' ? 'Norvège' : 'Norway'}</span>
                <span className="arrow">→</span>
                <span className="place">{language === 'fr' ? 'Nigéria' : 'Nigeria'}</span>
                <span className="arrow">→</span>
                <span className="place">{language === 'fr' ? 'Russie' : 'Russia'}</span>
                <span className="arrow">→</span>
                <span className="place">France</span>
              </div>
              <p className="journey-years">1978 — 2005</p>
            </div>
            
            <div className="bio-education">
              <h3 className="education-title">{language === 'fr' ? 'Formation' : 'Education'}</h3>
              <p>{language === 'fr' ? 'École des Beaux Arts de Bondi, Sydney, Australie (1983-1988)' : 'School of Fine Arts of Bondi, Sydney, Australia (1983-1988)'}</p>
              <p>{language === 'fr' ? 'Peinture chinoise' : 'Chinese Painting'}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Exhibitions Page Component
const ExhibitionsPage = ({ language, onLanguageToggle }) => {
  // Upcoming exhibitions
  const upcomingExhibitions = [
    {
      date: "18-21.02.2027",
      title: "Art en Capital 2027",
      venue: "Grand Palais, Paris",
      link: "https://www.artcapital.fr",
      image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_e2743e27-746f-4bbc-a99a-c2654031c850/artifacts/a261bd1f1142f48c_Avalanche.jpg",
      description: {
        fr: "Mercredi 18 février 2027 : Vernissage (sur invitation uniquement) - envoyez un email à elisabeth.benes@gmail.com. 19 au 21 février 2027 : Ouverture au public.",
        en: "Wednesday February 18, 2027: Private viewing (by invitation only) - send an email to elisabeth.benes@gmail.com. February 19-21, 2027: Open to the public."
      }
    },
    {
      date: "2027",
      title: "Variation sur la Matière",
      venue: "",
      link: "",
      image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_e2743e27-746f-4bbc-a99a-c2654031c850/artifacts/87436325551b98df_Matie%CC%80re.jpeg",
      description: {
        fr: "Exposition de la nouvelle Variation sur la Matière prévue pour 2027.",
        en: "Exhibition of the new Variation sur la Matière planned for 2027."
      }
    }
  ];
  
  // Group exhibitions by decade
  const getDecade = (year) => {
    const y = parseInt(year.split('-')[0]);
    if (y >= 2020) return '2020s';
    if (y >= 2010) return '2010s';
    if (y >= 2000) return '2000s';
    return '1990s';
  };
  
  const groupedExhibitions = EXHIBITIONS.reduce((acc, exhibition) => {
    const decade = getDecade(exhibition.year);
    if (!acc[decade]) acc[decade] = [];
    acc[decade].push(exhibition);
    return acc;
  }, {});

  const getTypeLabel = (type) => {
    const labels = {
      fr: {
        solo: 'Exposition personnelle',
        group: 'Exposition collective',
        salon: 'Salon',
        award: 'Prix',
        biennial: 'Biennale',
        retrospective: 'Rétrospective',
        creation: 'Création',
        study: 'Étude',
        research: 'Recherche',
        preparation: 'Préparation',
        'open-studio': 'Portes ouvertes'
      },
      en: {
        solo: 'Solo Exhibition',
        group: 'Group Exhibition',
        salon: 'Salon',
        award: 'Award',
        biennial: 'Biennial',
        retrospective: 'Retrospective',
        creation: 'Creation',
        study: 'Study',
        research: 'Research',
        preparation: 'Preparation',
        'open-studio': 'Open Studio'
      }
    };
    return labels[language][type] || type;
  };

  return (
    <div className="page-container" data-testid="exhibitions-page">
      <PageSEO page="exhibitions" />
      <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="exhibitions" />
      <main className="exhibitions-content">
        <div className="exhibitions-header">
          <h1 className="exhibitions-title">
            {language === 'fr' ? 'Expo & Historique' : 'Exhibitions & Timeline'}
          </h1>
        </div>
        
        {/* Upcoming Exhibitions Section */}
        <section className="upcoming-section" data-testid="upcoming-exhibitions">
          <h2 className="upcoming-title">
            {language === 'fr' ? 'Expositions à venir' : 'Upcoming Exhibitions'}
          </h2>
          
          {upcomingExhibitions.length > 0 ? (
            <div className="upcoming-list">
              {upcomingExhibitions.map((exhibition, index) => (
                <a 
                  key={index} 
                  href={exhibition.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="upcoming-item-link"
                  data-testid={`upcoming-exhibition-${index}`}
                >
                  <div className="upcoming-item upcoming-item-featured">
                    {exhibition.image && (
                      <div className="upcoming-image-wrapper">
                        <img 
                          src={exhibition.image} 
                          alt={exhibition.title}
                          className="upcoming-image"
                        />
                      </div>
                    )}
                    <div className="upcoming-content">
                      <div className="upcoming-date">{exhibition.date}</div>
                      <h3 className="upcoming-event-title">{exhibition.title}</h3>
                      <p className="upcoming-venue">{exhibition.venue}</p>
                      {exhibition.description && (
                        <p className="upcoming-description">
                          {exhibition.description[language] || exhibition.description.fr}
                        </p>
                      )}
                      <span className="upcoming-cta">
                        {language === 'fr' ? 'Voir le site →' : 'Visit website →'}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="upcoming-empty">
              <p className="updating-soon">
                {language === 'fr' 
                  ? 'Mise à jour prochaine pour les nouveaux événements et rencontres'
                  : 'Updating soon for new events and meetings'}
              </p>
            </div>
          )}
        </section>
        
        {/* Timeline Section */}
        <section className="timeline-section">
          <h2 className="timeline-header-title">
            {language === 'fr' ? 'Historique' : 'Timeline'}
          </h2>
          
          <div className="timeline-container">
            {Object.entries(groupedExhibitions).map(([decade, exhibitions]) => (
              <div key={decade} className="decade-section">
                <div className="decade-marker">
                  <span className="decade-label">{decade}</span>
                </div>
                
                <div className="decade-exhibitions">
                  {exhibitions.map((exhibition, index) => (
                    <div key={index} className={`timeline-item ${exhibition.type}`}>
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-year">{exhibition.year}</div>
                        <h3 className="timeline-title">{exhibition.title}</h3>
                        {exhibition.venue && (
                          <p className="timeline-venue">
                            {exhibition.venue}
                            {exhibition.country && `, ${exhibition.country}`}
                          </p>
                        )}
                        {exhibition.theme && (
                          <p className="timeline-theme">{exhibition.theme}</p>
                        )}
                        <span className="timeline-type">{getTypeLabel(exhibition.type)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

const PressPage = ({ language, onLanguageToggle }) => (
  <div className="page-container">
    <PageSEO page="press" />
    <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="press" />
    <main className="page-content centered">
      <h1>{language === 'fr' ? 'Presse' : 'Press'}</h1>
      <p className="coming-soon-text">{language === 'fr' ? 'Bientôt disponible...' : 'Coming soon...'}</p>
    </main>
  </div>
);

const ContactPage = ({ language, onLanguageToggle }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  return (
    <div className="page-container" data-testid="contact-page">
      <PageSEO page="contact" />
      <Header language={language} onLanguageToggle={onLanguageToggle} activeSection="contact" />
      <main className="contact-content">
        <div className="contact-layout">
          {/* Contact Image */}
          <div className="contact-image-section">
            <div className="contact-image-wrapper">
              {!imageLoaded && (
                <div className="contact-image-placeholder">
                  <div className="loading-spinner"></div>
                </div>
              )}
              <img 
                src="https://www.dropbox.com/scl/fi/d6eyl54fo69cao7hxuxat/BB_05.JPG?rlkey=3dhh50du2vq6o7hhruz503kzk&raw=1"
                alt="Elisabeth Troestler"
                className={`contact-image ${imageLoaded ? 'loaded' : ''}`}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="contact-info-section">
            <h1 className="contact-title">Contact</h1>
            
            <div className="contact-details">
              <div className="contact-item">
                <span className="contact-label">Email</span>
                <a href="mailto:Elisabeth.benes@gmail.com" className="contact-value contact-link">
                  Elisabeth.benes@gmail.com
                </a>
              </div>
              
              <div className="contact-item">
                <span className="contact-label">Mobile</span>
                <a href="tel:+33662533211" className="contact-value contact-link">
                  +33 6 62 53 32 11
                </a>
              </div>
            </div>
            
            {/* Company Information */}
            <div className="contact-company" data-testid="contact-company-info">
              <div className="contact-company-divider"></div>
              <div className="contact-company-details">
                <span className="contact-company-title">Artiste-auteur</span>
                <span className="contact-company-name">Elisabeth Troestler</span>
                <span className="contact-company-siret">SIRET: 999 595 598 00015</span>
                <span className="contact-company-location">92200 Neuilly-Sur-Seine</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  const [language, setLanguage] = useState('fr');
  
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/collections" element={<CollectionsPage language={language} onLanguageToggle={toggleLanguage} />} />
            <Route path="/collections/:collectionSlug" element={<CollectionsPage language={language} onLanguageToggle={toggleLanguage} />} />
            <Route path="/exhibitions" element={<ExhibitionsPage language={language} onLanguageToggle={toggleLanguage} />} />
            <Route path="/bio" element={<BioPage language={language} onLanguageToggle={toggleLanguage} />} />
            <Route path="/press" element={<PressPage language={language} onLanguageToggle={toggleLanguage} />} />
            <Route path="/contact" element={<ContactPage language={language} onLanguageToggle={toggleLanguage} />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
